import React, { useState, useEffect, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { getInstitucionesAdmin, cambiarEstadoInstitucion } from '../../services/institucionesService';
import { setInstitucionAdmin } from '../../services/authService';
import { useInstitucion } from '../../context/InstitucionContext';

// Instituciones de la plataforma (sólo SuperAdmin).
const AdminInstituciones = () => {
  const { institucion: activa, paleta: colors, seleccionar } = useInstitucion();
  const history = useHistory();

  const [instituciones, setInstituciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setInstituciones(await getInstitucionesAdmin());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const handleToggleEstado = async (inst) => {
    const accion = inst.activo ? 'desactivar' : 'activar';
    if (inst.activo && !window.confirm(`¿Desactivar "${inst.nombreCorto}"? Su sitio público dejará de responder y sus usuarios no podrán entrar.`)) {
      return;
    }
    try {
      await cambiarEstadoInstitucion(inst.id, !inst.activo);
      setInstituciones((prev) => prev.map((i) => (i.id === inst.id ? { ...i, activo: !i.activo } : i)));
    } catch (err) {
      alert(`No se pudo ${accion}: ${err.message}`);
    }
  };

  const handleEntrar = (inst) => {
    setInstitucionAdmin(inst.slug);
    seleccionar(inst.slug);
    history.push('/admin');
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Instituciones</h1>
          <p className="text-gray-500 text-sm mt-1">Empresas e instituciones que publican en la plataforma</p>
        </div>
        <Link
          to="/admin/instituciones/nueva"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white font-semibold rounded-lg shadow-md transition-all hover:opacity-90"
          style={{ background: `linear-gradient(135deg, ${colors.accentFrom}, ${colors.accentTo})` }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nueva institución
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">Cargando instituciones...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-500">{error}</div>
        ) : instituciones.length === 0 ? (
          <div className="p-12 text-center text-gray-400">Aún no hay instituciones.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-200">
                  <th className="px-4 py-3 font-semibold">Institución</th>
                  <th className="px-4 py-3 font-semibold hidden md:table-cell">Slug</th>
                  <th className="px-4 py-3 font-semibold hidden lg:table-cell">Dominios</th>
                  <th className="px-4 py-3 font-semibold">Estado</th>
                  <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {instituciones.map((inst) => (
                  <tr key={inst.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span
                          className="w-8 h-8 rounded-lg flex-shrink-0"
                          style={{ background: `linear-gradient(135deg, ${inst.colorPrimario || '#334155'}, ${inst.colorSecundario || '#0f172a'})` }}
                          aria-hidden="true"
                        />
                        <div>
                          <div className="font-medium text-gray-900">
                            {inst.nombreCorto}
                            {activa && activa.id === inst.id && (
                              <span className="ml-2 text-xs text-gray-400 font-normal">(activa)</span>
                            )}
                          </div>
                          <div className="text-gray-400 text-xs">{inst.nombre}</div>
                          {!inst.visibleEnPortal && (
                            <div className="text-gray-400 text-xs mt-0.5" title="No aparece en el directorio ni en la vitrina de la plataforma">
                              No se muestra en el portal
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 font-mono hidden md:table-cell">{inst.slug}</td>
                    <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">
                      {inst.dominios.length === 0
                        ? <span className="text-gray-400">Sólo en la plataforma</span>
                        : inst.dominios.map((d) => d.host).join(', ')}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleEstado(inst)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          inst.activo
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                        title="Clic para cambiar el estado"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${inst.activo ? 'bg-green-500' : 'bg-gray-400'}`} />
                        {inst.activo ? 'Activa' : 'Inactiva'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {inst.activo && (
                          <button
                            onClick={() => handleEntrar(inst)}
                            className="text-xs font-medium px-2.5 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                            title="Usarla como institución activa en el panel"
                          >
                            Entrar
                          </button>
                        )}
                        <Link
                          to={`/admin/instituciones/${inst.id}/editar`}
                          className="text-gray-500 hover:text-gray-900 p-1.5 rounded-md hover:bg-gray-100"
                          title="Editar"
                        >
                          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInstituciones;
